import InputText from "../../components/Forms/InputText/InputText";
import { routes } from "../../shared/router/routes";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useContext, useState } from "react";
import { LoadingContext } from "../../shared/providers/loading/loading.provider";
import Success from "./../../shared/assets/imgs/graphic_1.svg";
import { TranslationContext } from "../../shared/providers/translation/translation.provider";
import Button from "../../components/Button/Button";
import { useForgotPassword } from "../../shared/hooks/useAuthentication";
import Logo from "../../components/Logo/Logo";
import Link from "../../components/Link/Link";

function PasswordRecovery() {
    const { setIsLoading } = useContext(LoadingContext);
    const [isSuccess, setIsSuccess] = useState<boolean | undefined>();
    const { translate } = useContext(TranslationContext);
    const forgotPassword = useForgotPassword();

    const form = useFormik({
        initialValues: {
            email: ""
        },
        validationSchema: Yup.object().shape({
            email: Yup.string()
                .email(
                    translate("page.forgot_password.invalid_email", "Insert a valid email please")
                )
                .required(translate("page.forgot_password.no_email", "Insert email please"))
        }),
        validateOnChange: true,
        onSubmit: async (values: { email: string }) => {
            setIsLoading(true);

            const response = await forgotPassword.mutateAsync(values.email);
            setIsSuccess(response.success);
            setIsLoading(false);
        }
    });

    return (
        <>
            {isSuccess === undefined && (
                <>
                    <Logo className="d-none d-md-block" />
                    <div className="d-flex flex-column gap-2 w-100">
                        <h1 className="login_title text-h1-big-bd text-md-center">
                            {translate("page.forgot_password.title", "Reset password")}
                        </h1>
                        <form
                            className="form d-flex flex-column gap-2"
                            onSubmit={form.handleSubmit}>
                            <InputText
                                name="email"
                                inputMode="email"
                                label={translate("common.email", "Email")}
                                placeholder={translate("form.placeholder.email", "Insert email")}
                                type="text"
                                customCSS={form.errors.email ? "has_error" : ""}
                                errors={form.submitCount > 0 ? form.errors.email?.split("/") : []}
                                onChange={form.handleChange}
                            />
                            <Button
                                type="submit"
                                variant="solid"
                                customCss="btn_action mt-n02 mt-md-0 align-self-center"
                                label={translate("password.recover_password", "Recover password")}
                            />
                        </form>
                    </div>
                </>
            )}
            {!isSuccess && isSuccess !== undefined && (
                <div className="login_message w-100 d-flex flex-column justify-content-center align-items-center text-center gap-1">
                    <img src={Success} alt="" aria-hidden="true" className="mb-2" />
                    <h1 className="text-h1-md title px-md-5 mx-md-5">
                        {translate("common.generic_error", "There was an error")}
                    </h1>
                    <span className="message text-bd1-rg">
                        {translate("common.retry", "Please try again.")}
                    </span>
                    <button
                        onClick={() => setIsSuccess(undefined)}
                        className="button btn_action bg-green-500 color-neutrals-black text-bd2-bd">
                        {translate("page.login.change_password", "Change password")}
                    </button>
                </div>
            )}
            {isSuccess && (
                <div className="login_message w-100 d-flex flex-column justify-content-center align-items-center text-center gap-1">
                    <img src={Success} alt="" aria-hidden="true" className="mb-2" />
                    <h1 className="text-h1-md title px-md-5 mx-md-5">
                        {translate("page.login.forgot_password.success", "Check your email!")}
                    </h1>
                    <span className="text-bd1-rg message">
                        {translate(
                            "page.login.password_success_description",
                            "We've sent a password reset link to your email. Please follow the instructions in the email to proceed with setting up your new password."
                        )}
                    </span>
                    <Link
                        variant="outlined"
                        to={routes.LOGIN.path}
                        customCss="button btn_action text-bd2-bd">
                        {translate("page.login.go_to_login", "Go to login")}
                    </Link>
                </div>
            )}
        </>
    );
}

export default PasswordRecovery;
